/**
 * D-1018-3: **the rail's frame, written once.** Four apps draw a left rail (console, bank, dealer, the cabinet's
 * desk) and each had its own copy of these classes; the copies had drifted into the three faults the founder
 * saw: a rail that scrolled away with the page, labels wider than the box that holds them, and anchors underlined by
 * the browser's own default. The drawing's rail (`hakili-console.jsx`, `NavRail`) is the model; two things differ
 * by the founder's ruling: it is **96 px wide** (`w-24`, the drawing's `w-20` cuts « Fonctionnalités »), and its
 * labels are whole words.
 *
 * - **`RAIL_FRAME`**: the full height of the window and pinned to it (`sticky top-0 h-screen`), so only the content
 *   scrolls; `overflow-hidden` so nothing of it ever spills onto the page. The bottom padding (`py-5`) leaves the
 *   room the build stamp (`#build-marker`, fixed at the window's bottom-left) needs on the apps that draw it: it overlaps nothing.
 *   The bank draws none (`BANK-WEB-STAMP-DROP`, D-1026-1) and sets its own padding.
 * - **`RAIL_NAV`**: the entries take what height is left and scroll INSIDE the rail when the window is too short (from 720 px
 *   of height nothing scrolls),
 *   so the foot (idea, bell, logout, avatar) is never pushed off the screen.
 * - **`RAIL_ENTRY`**: an icon over a whole word, centred, with no underline (an anchor's default, which the
 *   drawing does not have). The active entry wears `RAIL_ACTIVE`, the others `RAIL_IDLE`; `RAIL_UNBUILT` is the
 *   entry its round has not built yet.
 * - **`RAIL_FOOT`**: an item of the foot never shrinks.
 */
export const RAIL_FRAME =
  "sticky top-0 flex h-screen w-24 shrink-0 flex-col items-center overflow-hidden border-r border-gray-200 bg-white pb-5 pt-3";
export const RAIL_NAV =
  "flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
export const RAIL_ENTRY =
  "flex w-full shrink-0 flex-col items-center gap-0.5 px-1 py-1.5 text-center leading-tight no-underline transition-colors";
export const RAIL_ACTIVE = "bg-indigo-50 text-indigo-600";
export const RAIL_IDLE = "text-gray-500 hover:bg-indigo-50 hover:text-indigo-600";
export const RAIL_UNBUILT = "text-gray-300";
export const RAIL_FOOT = "shrink-0";

/**
 * DX-005, **the foot, compact**: the idea, the bell, the way out and the person are ICONS (each named by its
 * `aria-label` and its tooltip), two to a row, so the whole foot is about 64 px instead of 150 and the entries above
 * it keep their room. « Une idée ? » stays IN the rail on every view; it is never behind a menu.
 *
 * - **`RAIL_FOOT_BAR`**: the pinned foot, a two-column grid under a hairline; it never shrinks.
 * - **`RAIL_FOOT_ICON`**: one 28 px control; `RAIL_FOOT_SPAN` makes a lone last control take the whole row.
 * - **`RAIL_FOOT_AVATAR`**: the person's initials, 28 px, in the app's own colour (passed by the shell).
 * - **`RAIL_MARK`**: the app's name under the mark ( « interne », « bank » ), `gray-500` for AA contrast (D-1018-6).
 */
export const RAIL_FOOT_BAR =
  "mt-1 grid w-full shrink-0 grid-cols-2 justify-items-center gap-1 border-t border-gray-100 px-2 pt-1";
export const RAIL_FOOT_ICON =
  "relative flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-indigo-50 hover:text-indigo-600";
export const RAIL_FOOT_SPAN = "col-span-2";
export const RAIL_FOOT_AVATAR =
  "flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold";
export const RAIL_MARK = "mb-3 shrink-0 text-xs font-medium text-gray-500";

/*
 * `D-1019-1`, the nav's scrollbar: **it takes no width from the labels.** A classic scrollbar (Windows) is 15 px of the
 * nav's own box, and the labels are sized to what is left; it was the difference between « Développement » whole and
 * « éveloppemen ». The nav still scrolls (wheel, touch, keyboard, focus) when the window is under 720 px of height, with no
 * visible bar, and never sideways: `overflow-x-hidden` is the guard, set AFTER the labels fit (the fit check measures the
 * labels themselves, not the guard).
 */
